import React, { useMemo, useState } from 'react';
import { paymentPlanApi, formatApiError } from '../services/api';
import { Button } from './ui/button';
import { X, Calendar, CurrencyEur } from '@phosphor-icons/react';
import { toast } from 'sonner';

const fmtEUR = (v) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));

const defaultFirstDue = () => {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().slice(0, 10);
};

/**
 * Modal zum Anlegen eines gleichmäßigen Monats-Ratenplans.
 * Props:
 *  - open, onClose, onSaved (fn)
 *  - invoice: { invoice_id, invoice_number, open_amount, customer_name }
 */
export default function PaymentPlanDialog({ open, onClose, onSaved, invoice }) {
  const [months, setMonths] = useState(3);
  const [firstDue, setFirstDue] = useState(defaultFirstDue());
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const preview = useMemo(() => {
    if (!invoice || months < 2) return [];
    const total = Number(invoice.open_amount || 0);
    const base = Math.round((total / months) * 100) / 100;
    const rows = [];
    let running = 0;
    const start = new Date(firstDue);
    for (let i = 1; i <= months; i++) {
      const d = new Date(start);
      d.setMonth(start.getMonth() + (i - 1));
      // Wenn Tag über den Monatsletzten hinaus wandert, korrigiert JS automatisch
      const amount = i < months ? base : Math.round((total - running) * 100) / 100;
      running = Math.round((running + amount) * 100) / 100;
      rows.push({ n: i, amount, due: d.toISOString().slice(0, 10) });
    }
    return rows;
  }, [invoice, months, firstDue]);

  if (!open || !invoice) return null;

  const totalPreview = preview.reduce((s, r) => s + r.amount, 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      await paymentPlanApi.createAuto(invoice.invoice_id, {
        months: Number(months),
        first_due_date: firstDue,
        notes: notes || undefined,
      });
      toast.success('Ratenplan erstellt', {
        description: `${months} Raten · Mahnwesen pausiert solange Zahlungen fristgerecht eingehen.`,
      });
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="payment-plan-dialog"
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E4E9]">
          <div>
            <h2 className="text-xl font-bold text-[#111418]">Ratenplan anlegen</h2>
            <p className="text-sm text-[#525866]">
              Rechnung <b>{invoice.invoice_number}</b> · {invoice.customer_name} ·{' '}
              <b>{fmtEUR(invoice.open_amount)}</b>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded"
            data-testid="payment-plan-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#525866] mb-1">
                Anzahl Monatsraten
              </label>
              <input
                type="number"
                min={2}
                max={60}
                value={months}
                onChange={(e) => setMonths(Math.max(2, Math.min(60, Number(e.target.value) || 2)))}
                className="w-full px-3 py-2 border border-[#E2E4E9] rounded focus:outline-none focus:ring-2 focus:ring-[#E60000]"
                data-testid="payment-plan-months"
              />
              <p className="text-xs text-[#8A94A6] mt-1">Alle Raten gleich hoch (letzte gleicht Rundung aus).</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#525866] mb-1">
                Fälligkeit 1. Rate
              </label>
              <input
                type="date"
                value={firstDue}
                onChange={(e) => setFirstDue(e.target.value)}
                className="w-full px-3 py-2 border border-[#E2E4E9] rounded focus:outline-none focus:ring-2 focus:ring-[#E60000]"
                data-testid="payment-plan-first-due"
              />
              <p className="text-xs text-[#8A94A6] mt-1">Folgeraten jeweils 1 Monat später.</p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#525866] mb-1">Notiz (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="z. B. Interne Absprache mit dem Kunden"
              className="w-full px-3 py-2 border border-[#E2E4E9] rounded focus:outline-none focus:ring-2 focus:ring-[#E60000]"
              data-testid="payment-plan-notes"
            />
          </div>

          {/* Preview */}
          <div className="border border-[#E2E4E9] rounded-lg overflow-hidden">
            <div className="bg-[#FAFAFB] px-4 py-2 flex items-center justify-between">
              <p className="text-sm font-semibold text-[#111418]">Vorschau</p>
              <p className="text-xs text-[#525866]">
                Summe:{' '}
                <b className={Math.abs(totalPreview - Number(invoice.open_amount)) > 0.02 ? 'text-red-600' : 'text-emerald-600'}>
                  {fmtEUR(totalPreview)}
                </b>
              </p>
            </div>
            <table className="w-full text-sm" data-testid="payment-plan-preview">
              <thead className="bg-white">
                <tr className="text-left text-xs text-[#8A94A6] border-b border-[#E2E4E9]">
                  <th className="px-4 py-2">Rate</th>
                  <th className="px-4 py-2">Fällig</th>
                  <th className="px-4 py-2 text-right">Betrag</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((r) => (
                  <tr key={r.n} className="border-b border-[#F0F1F3] last:border-0">
                    <td className="px-4 py-2 font-medium">{r.n}. Rate</td>
                    <td className="px-4 py-2 text-[#525866]">
                      <Calendar weight="bold" size={12} className="inline mr-1 text-[#8A94A6]" />
                      {r.due}
                    </td>
                    <td className="px-4 py-2 text-right font-semibold">{fmtEUR(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="text-xs text-[#525866] bg-amber-50 border border-amber-200 rounded p-3">
            <b>Hinweis:</b> Solange keine Rate mehr als 7 Tage überfällig ist, wird das Mahnwesen für diese Rechnung
            pausiert. Sobald eine Rate diese Frist überschreitet, wird die Rechnung wieder mahnfähig.
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-3 border-t border-[#E2E4E9] bg-white">
          <Button variant="outline" onClick={onClose} data-testid="payment-plan-cancel">
            Abbrechen
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || months < 2}
            className="btn-primary"
            data-testid="payment-plan-save"
          >
            <CurrencyEur weight="bold" size={14} className="mr-1" />
            {saving ? 'wird angelegt…' : 'Ratenplan aktivieren'}
          </Button>
        </div>
      </div>
    </div>
  );
}
